import StorefrontIcon from "@mui/icons-material/Storefront";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { isStoreApiReady } from "../constants/organizationStoreRegistry";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { WooStoreConfig } from "../types";
import {
  getStoreDocumentStatusChip,
  type StoreDocumentOpState,
} from "../helpers/storeDocumentStatusChip";

interface CannabinoidStorePickerProps {
  stores: WooStoreConfig[];
  selectedStoreId: string | null;
  onSelectStore: (store: WooStoreConfig) => void;
  /** institutionIds com consentimento ativo — demais ativos exibem badge. */
  consentedInstitutionIds?: Set<string>;
  /** grid = tela inicial; list = sidebar vertical compacta */
  variant?: "grid" | "list";
  /** Status documental por institutionId/CNPJ da loja. */
  storeOpStateByOrgId?: Record<string, StoreDocumentOpState>;
}

function resolveLogoSrc(store: WooStoreConfig): string | undefined {
  return store.logoDataUrl ?? store.logoUrl;
}

function StoreCardContent({
  store,
  isSelected,
  isInactive,
  needsConsent,
  documentStatus,
  variant,
}: {
  store: WooStoreConfig;
  isSelected: boolean;
  isInactive: boolean;
  needsConsent: boolean;
  documentStatus?: StoreDocumentOpState;
  variant: "grid" | "list";
}) {
  const theme = useTheme();
  const logoSrc = resolveLogoSrc(store);

  const statusChip = documentStatus ? (
    <Chip
      label={getStoreDocumentStatusChip(documentStatus).label}
      size="small"
      color={getStoreDocumentStatusChip(documentStatus).color}
      sx={{
        height: 22,
        fontSize: "0.7rem",
        fontWeight: 700,
        maxWidth: "100%",
      }}
    />
  ) : null;

  const consentBadge = needsConsent ? (
    <Tooltip title="Autorização necessária para solicitar dispensação">
      <Chip
        icon={<VerifiedUserOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Autorização necessária"
        size="small"
        color="warning"
        sx={{
          height: 24,
          fontSize: "0.75rem",
          fontWeight: 700,
          maxWidth: "100%",
        }}
      />
    </Tooltip>
  ) : null;

  if (variant === "list") {
    return (
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ px: 1.25, py: 1, minHeight: 72 }}
      >
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={store.name}
            sx={{
              width: 48,
              height: 48,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary800.main, 0.1),
              color: "primary800.main",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <StorefrontIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ ...mt.label }} noWrap>
            {store.name}
          </Typography>
          {isInactive ? (
            <Chip
              label="Em breve"
              size="small"
              sx={{
                mt: 0.25,
                height: 24,
                fontSize: "0.8125rem",
                fontWeight: 700,
              }}
            />
          ) : (
            <Stack spacing={0.5} alignItems="flex-start">
              {statusChip}
              {consentBadge}
            </Stack>
          )}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={0.75}
      alignItems="center"
      justifyContent="center"
      sx={{ px: 1.25, py: 1.25, minHeight: 128, position: "relative" }}
    >
      {isInactive ? (
        <Chip
          label="Em breve"
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            height: 24,
            fontSize: "0.8125rem",
            fontWeight: 700,
          }}
        />
      ) : (
        <Stack
          spacing={0.5}
          sx={{ position: "absolute", top: 8, right: 8, left: 8 }}
          alignItems="flex-end"
        >
          {statusChip}
          {needsConsent ? consentBadge : null}
        </Stack>
      )}
      {logoSrc ? (
        <Box
          component="img"
          src={logoSrc}
          alt={store.name}
          sx={{ width: "100%", height: 52, objectFit: "contain", p: 0.5 }}
        />
      ) : (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary800.main, 0.1),
            color: "primary800.main",
            display: "grid",
            placeItems: "center",
          }}
        >
          <StorefrontIcon />
        </Box>
      )}
      <Typography
        fontWeight={700}
        textAlign="center"
        sx={{
          ...mt.bodySecondary,
          color: isSelected ? "primary800.main" : "text.primary",
        }}
      >
        {store.name}
      </Typography>
    </Stack>
  );
}

/** Seleção de dispensadores — grid compacto ou lista vertical na sidebar. */
export function CannabinoidStorePicker({
  stores,
  selectedStoreId,
  onSelectStore,
  consentedInstitutionIds,
  storeOpStateByOrgId,
  variant = "grid",
}: CannabinoidStorePickerProps) {
  const theme = useTheme();

  if (stores.length === 0) {
    return (
      <Typography sx={{ ...mt.body, color: "text.secondary" }} textAlign="center">
        Nenhum dispensador disponível no momento.
      </Typography>
    );
  }

  const renderStore = (store: WooStoreConfig) => {
    const isSelected = selectedStoreId === store.id;
    const isInactive = !isStoreApiReady(store);
    const needsConsent =
      !isInactive &&
      Boolean(store.institutionId) &&
      !consentedInstitutionIds?.has(store.institutionId ?? "");
    const documentStatus = store.institutionId
      ? storeOpStateByOrgId?.[store.institutionId]
      : undefined;

    return (
      <Card
        key={store.id}
        variant="outlined"
        sx={{
          borderRadius: 1.5,
          opacity: isInactive ? 0.45 : 1,
          pointerEvents: isInactive ? "none" : "auto",
          borderColor: isSelected
            ? "primary800.main"
            : alpha(theme.palette.primary800.main, 0.14),
          bgcolor: isSelected
            ? alpha(theme.palette.primary800.main, 0.06)
            : "background.paper",
        }}
      >
        <CardActionArea
          disabled={isInactive}
          onClick={() => onSelectStore(store)}
        >
          <StoreCardContent
            store={store}
            isSelected={isSelected}
            isInactive={isInactive}
            needsConsent={needsConsent}
            documentStatus={documentStatus}
            variant={variant}
          />
        </CardActionArea>
      </Card>
    );
  };

  if (variant === "list") {
    return <Stack spacing={0.75}>{stores.map(renderStore)}</Stack>;
  }

  return (
    <Grid container spacing={1.25}>
      {stores.map((store) => (
        <Grid item xs={6} sm={4} md={3} key={store.id}>
          {renderStore(store)}
        </Grid>
      ))}
    </Grid>
  );
}
